import type { ReactNode } from 'react';
import styles from './CharacterAvatar.module.css';

/**
 * **`CharacterAvatar`**（§4.6 / D101）— エクマル。
 *
 * ## 表情はアプリが決定的に決める（規約 6）
 *
 * **`DialogueAct` の種類から決定的に写像する。LLM に表情を選ばせない。**
 * INV-2 の系であり、**D86 決定4（`strength` はアプリが決める）と同じ形**。
 *
 * ## 正解への近さを表情で伝えない（規約 7）
 *
 * **「惜しい」「いいですね」に対応する表情差分を作らない。**
 * 5 状態はすべて **AI 自身の行為かセッションの段階**を指しており、
 * **学習者の答えの当たり具合を指していない**。
 *
 * **これは INV-5 の新しい漏洩経路である** — 表情は境界 Validator の外側にあり、
 * **`displayLabel` の検査では捕まらない**。
 * `celebrating` が `COMPLETED` 限定なのも同じ理由（**そこでは既に開示済み**）。
 *
 * ## `thinking` を不可視フェーズに出さない
 *
 * **初回ターンの不可視フェーズに「考えている」を出すと §2.2 の隠蔽が壊れる。**
 * **出すのは可視の工程表示のときだけ**（呼び出し側が守る規約）。
 *
 * ## `Avatar`（人）と統合しない（§6）
 *
 * **統合すると「キャラクターが人として並ぶ」画面ができる。**
 *
 * ## フォーカス可能にしない（規約 2）
 *
 * **`onClick` を受け取らない型にしてある** —
 * **eye `#ffcf00` と DADS のフォーカス黄 `#ffd43d` は相互 1.04:1 = ほぼ同色**で、
 * **リングが目に埋もれてフォーカスが読めなくなる**。
 */
export type CharacterState =
  /** 通常の発話 */
  | 'neutral'
  /** **診断質問を投げたターン**（「これはあなたに聞いている」） */
  | 'asking'
  /** **可視の工程表示中のみ**（不可視フェーズには出さない） */
  | 'thinking'
  /** **「答えだけ教えて」を断ったとき**（D2）と注意書きの脇（D13） */
  | 'no-answer'
  /** **`COMPLETED` 成立時のみ**（D17） */
  | 'celebrating';

/**
 * **隅の小バッジ**（規約 3。会話内 32px では表情の差が読めないため）。
 * **`neutral` にはバッジを出さない**（素の顔がそれである）。
 */
const BADGE: Readonly<Record<CharacterState, { readonly glyph: string; readonly name: string } | undefined>> = {
  neutral: undefined,
  asking: { glyph: '?', name: 'あなたに聞いています' },
  thinking: { glyph: '…', name: '考えています' },
  'no-answer': { glyph: '×', name: '答えは教えません' },
  celebrating: { glyph: '♥', name: '解けました' },
};

export interface CharacterAvatarProps {
  /** 加工不要（原本をそのまま使う。`?raw` で読み込んだ文字列） */
  readonly svg: string;
  /**
   * **アプリが決定的に決めた状態**（規約 6）。
   * **LLM に選ばせない。**
   */
  readonly state?: CharacterState;
}

export function CharacterAvatar({ svg, state = 'neutral' }: CharacterAvatarProps): ReactNode {
  const badge = BADGE[state];
  return (
    <span className={styles['wrap']}>
      <span className={styles['image']} aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />
      {badge === undefined ? null : (
        /** **記号だけで意味を伝えない** — 読み上げに名前を出す */
        <span className={styles['badge']} role="img" aria-label={badge.name}>
          {badge.glyph}
        </span>
      )}
    </span>
  );
}

/**
 * **発話の頭に置く名前つきの行**（規約 4: **連続発話でアバターを繰り返さない**）。
 * **繰り返さない判断は呼び出し側が持つ**（この部品は「出す」だけ）。
 */
export function CharacterHeader({
  svg,
  state,
  name = 'エクマル',
}: CharacterAvatarProps & { readonly name?: string }): ReactNode {
  return (
    <div className={styles['row']}>
      <CharacterAvatar svg={svg} {...(state === undefined ? {} : { state })} />
      <span className={styles['name']}>{name}</span>
    </div>
  );
}
